import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAllMedicalTextIssuesCounts } from "@/hooks/useAllMedicalTextIssuesCounts";
import { supabase } from "@/integrations/supabase/client";
import { Pencil, Search, X } from "lucide-react";
import { toast } from "sonner";
import { NewMedicalTextDialog } from "./NewMedicalTextDialog";
import useTestReports from "./useTestReports";
import { useState } from "react";

const IndividualReports = () => {
  const [focusedMedicalTextId, setFocusedMedicalTextId] = useState<string | null>(null);
  const [isMedicalTextDialogOpen, setIsMedicalTextDialogOpen] = useState(false);
  const [searchCode, setSearchCode] = useState("");
  const { getIssueCount } = useAllMedicalTextIssuesCounts();

  const {
    data: examples,
    isLoading,
    refetch,
  } = useTestReports({
    searchCode: searchCode || undefined,
  });

  const toggleCheckedByCoder = async (id: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from("medical_text_examples")
        .update({ checked_by_coder: !currentValue })
        .eq("id", id);

      if (error) throw error;

      // Refetch data to update UI
      refetch();

      toast.success(`${!currentValue ? "Marked" : "Unmarked"} as checked by clinical coder`);
    } catch (err) {
      console.error("Error updating checked status:", err);
      toast.error("Failed to update checked status");
    }
  };

  const toggleFollowUp = async (id: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from("medical_text_examples")
        .update({ is_follow_up: !currentValue })
        .eq("id", id);

      if (error) throw error;

      // Refetch data to update UI
      refetch();

      toast.success(`${!currentValue ? "Marked" : "Unmarked"} as follow-up`);
    } catch (err) {
      console.error("Error updating follow-up status:", err);
      toast.error("Failed to update follow-up status");
    }
  };

  const getRowClassName = (exampleId: string) => {
    const issueCount = getIssueCount(exampleId);
    return issueCount > 0 ? "bg-red-50 border-red-100 hover:bg-red-100" : "hover:bg-muted/50";
  };

  const renderIssueIndicator = (exampleId: string) => {
    const issueCount = getIssueCount(exampleId);

    if (issueCount === 0) {
      return null;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-red-600 text-lg cursor-help ml-2">❌</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {issueCount} outstanding issue{issueCount !== 1 ? "s" : ""}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <div className="space-y-4">
      {isMedicalTextDialogOpen && (
        <NewMedicalTextDialog
          open={isMedicalTextDialogOpen}
          onClose={() => {
            setIsMedicalTextDialogOpen(false);
            setFocusedMedicalTextId(null);
          }}
          medicalTextId={focusedMedicalTextId ?? undefined}
        />
      )}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by ICD-10 or OPCS-4 code (e.g., I21.0, K28.1)"
            value={searchCode}
            onChange={(e) => setSearchCode(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchCode && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
              onClick={() => setSearchCode("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        {searchCode && (
          <div className="text-sm text-muted-foreground">
            {isLoading ? "Searching..." : `${examples?.length || 0} result${examples?.length !== 1 ? "s" : ""} found`}
          </div>
        )}
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Label (Seq #)</TableHead>
              <TableHead>Text</TableHead>
              <TableHead className="w-32">Type</TableHead>
              <TableHead className="w-32">Patient</TableHead>
              <TableHead className="w-32">Encounter</TableHead>
              <TableHead className="w-40">Checked</TableHead>
              <TableHead className="w-40">Follow-up</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : examples?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No examples found
                </TableCell>
              </TableRow>
            ) : (
              examples?.map((example) => (
                <TableRow key={example.id} className={getRowClassName(example.id)}>
                  <TableCell className="font-medium">
                    <div className="flex items-center">
                      {example.label}{" "}
                      {`${example.sequence_number}` !== example.label && (
                        <span className="text-gray-400 italic">({example.sequence_number})</span>
                      )}
                      {renderIssueIndicator(example.id)}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <div className="truncate">{example.text}</div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-block px-2 py-1 rounded-full text-sm ${
                        example.test_type === "Endoscopy"
                          ? "bg-blue-100 text-blue-800"
                          : example.test_type === "Histology"
                            ? "bg-green-100 text-green-800"
                            : example.test_type === "GP Summary"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {example.test_type || "Unspecified"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{example.patient_info?.id || "No patient"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{example.encounter_info?.integration_encounter_id || "No encounter"}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`checked-${example.id}`}
                        checked={example.checked_by_coder}
                        onCheckedChange={() => toggleCheckedByCoder(example.id, example.checked_by_coder)}
                      />
                      <label htmlFor={`checked-${example.id}`} className="text-sm cursor-pointer">
                        Clinical Coder
                      </label>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`follow-up-${example.id}`}
                        checked={example.is_follow_up || false}
                        onCheckedChange={() => toggleFollowUp(example.id, example.is_follow_up || false)}
                      />
                      <label htmlFor={`follow-up-${example.id}`} className="text-sm cursor-pointer">
                        Follow-up
                      </label>
                    </div>
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFocusedMedicalTextId(example.id);
                        setIsMedicalTextDialogOpen(true);
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default IndividualReports;
